import { createContext, use, useCallback, useMemo, useState, type ReactNode } from 'react';
import { apiRequest } from '../lib/api';
import { clearAuthHeaders, loadAuthHeaders } from '../lib/auth-storage';

export interface CurrentUser {
  email: string;
  allow_password_change: boolean;
}

export type AuthStatus = 'authenticated' | 'unauthenticated';

export type LoginResult =
  | { kind: 'authenticated'; user: CurrentUser }
  | { kind: 'otp_required'; challengeToken: string }
  | { kind: 'otp_enrollment_required'; challengeToken: string };

export interface AuthContextValue {
  status: AuthStatus;
  currentUser: CurrentUser | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyOtp: (challengeToken: string, code: string) => Promise<CurrentUser>;
  enrollOtp: (challengeToken: string) => Promise<{ otpauthUri: string; secret: string }>;
  confirmOtp: (
    challengeToken: string,
    code: string,
  ) => Promise<{ user: CurrentUser; recoveryCodes: string[] }>;
  changePassword: (password: string, password_confirmation: string) => Promise<CurrentUser>;
  logout: () => Promise<void>;
}

const USER_STORAGE_KEY = 'operator_console.user';

function loadUser(): CurrentUser | null {
  const raw = localStorage.getItem(USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    const u = JSON.parse(raw) as Partial<CurrentUser>;
    if (typeof u.email !== 'string' || typeof u.allow_password_change !== 'boolean') return null;
    return u as CurrentUser;
  } catch {
    return null;
  }
}

function saveUser(u: CurrentUser): void {
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(u));
}

function clearUser(): void {
  localStorage.removeItem(USER_STORAGE_KEY);
}

function initialState(): { status: AuthStatus; currentUser: CurrentUser | null } {
  const headers = loadAuthHeaders();
  const user = loadUser();
  if (headers && user) return { status: 'authenticated', currentUser: user };
  // partial state — treat as logged out, clean up
  if (headers && !user) clearAuthHeaders();
  if (!headers && user) clearUser();
  return { status: 'unauthenticated', currentUser: null };
}

const AuthContext = createContext<AuthContextValue | null>(null);

const CONSOLE_AUTH = '/loot_machine/api/operator_console/v1/auth';

interface SignInResponse {
  data: { email: string; allow_password_change: boolean };
}

interface ChallengeResponse {
  status: 'otp_required' | 'otp_enrollment_required';
  challenge_token: string;
}

interface EnrollResponse {
  otpauth_uri: string;
  secret: string;
}

type ConfirmResponse = SignInResponse & { recovery_codes: string[] };

function toUser(body: SignInResponse): CurrentUser {
  return {
    email: body.data.email,
    allow_password_change: body.data.allow_password_change,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [{ status, currentUser }, setState] = useState(initialState);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const res = await apiRequest<SignInResponse | ChallengeResponse>(`${CONSOLE_AUTH}/sign_in`, {
      method: 'POST',
      body: { email, password },
    });
    if ('status' in res.body) {
      return { kind: res.body.status, challengeToken: res.body.challenge_token };
    }
    const user = toUser(res.body);
    saveUser(user);
    setState({ status: 'authenticated', currentUser: user });
    return { kind: 'authenticated', user };
  }, []);

  const verifyOtp = useCallback(async (challengeToken: string, code: string): Promise<CurrentUser> => {
    const res = await apiRequest<SignInResponse>(`${CONSOLE_AUTH}/otp/verify`, {
      method: 'POST',
      body: { challenge_token: challengeToken, code },
    });
    const user = toUser(res.body);
    saveUser(user);
    setState({ status: 'authenticated', currentUser: user });
    return user;
  }, []);

  const enrollOtp = useCallback(async (challengeToken: string) => {
    const res = await apiRequest<EnrollResponse>(`${CONSOLE_AUTH}/otp/enroll`, {
      method: 'POST',
      body: { challenge_token: challengeToken },
    });
    return { otpauthUri: res.body.otpauth_uri, secret: res.body.secret };
  }, []);

  const confirmOtp = useCallback(
    async (challengeToken: string, code: string): Promise<{ user: CurrentUser; recoveryCodes: string[] }> => {
      const res = await apiRequest<ConfirmResponse>(`${CONSOLE_AUTH}/otp/confirm`, {
        method: 'POST',
        body: { challenge_token: challengeToken, code },
      });
      const user = toUser(res.body);
      saveUser(user);
      setState({ status: 'authenticated', currentUser: user });
      return { user, recoveryCodes: res.body.recovery_codes };
    },
    [],
  );

  const changePassword = useCallback<AuthContextValue['changePassword']>(
    async (password, password_confirmation) => {
      const res = await apiRequest<SignInResponse>('/api/web/v1/auth/password', {
        method: 'PUT',
        body: { password, password_confirmation },
      });
      // Devise returns the user object on success; allow_password_change should now be false.
      const user: CurrentUser = {
        email: res.body.data.email,
        allow_password_change: res.body.data.allow_password_change,
      };
      saveUser(user);
      setState({ status: 'authenticated', currentUser: user });
      return user;
    },
    [],
  );

  const logout = useCallback<AuthContextValue['logout']>(async () => {
    try {
      await apiRequest(`${CONSOLE_AUTH}/sign_out`, { method: 'DELETE' });
    } catch {
      // Best-effort. Even if backend call fails, we wipe local state below.
    }
    clearAuthHeaders();
    clearUser();
    setState({ status: 'unauthenticated', currentUser: null });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      currentUser,
      login,
      verifyOtp,
      enrollOtp,
      confirmOtp,
      changePassword,
      logout,
    }),
    [status, currentUser, login, verifyOtp, enrollOtp, confirmOtp, changePassword, logout],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
